import type { Faq } from '@/constants/home'

export type Plan = {
    id: 'starter' | 'growth' | 'scale'
    name: string
    blurb: string
    monthly: number
    yearly: number
    cta: string
    highlighted: boolean
    features: Array<string>
}

export const plans: Array<Plan> = [
    {
        id: 'starter',
        name: 'Starter',
        blurb: 'For a brand finding its voice on one or two apps.',
        monthly: 1450,
        yearly: 1190,
        cta: 'Start with Starter',
        highlighted: false,
        features: [
            '12 posts a month, made in one shoot',
            '4 short videos',
            '2 platforms',
            'A monthly ideas list',
            'Replies to comments on weekdays',
            'A one page monthly read',
            'A monthly call with your strategist'
        ]
    },
    {
        id: 'growth',
        name: 'Growth',
        blurb: 'For a brand ready to be talked about, properly.',
        monthly: 2900,
        yearly: 2390,
        cta: 'Get a forecast',
        highlighted: true,
        features: [
            '24 posts a month',
            '10 short videos',
            '3 platforms',
            'The weekly signal brief',
            'Daily community care',
            'A read every two weeks',
            'Two strategy calls a month',
            'Paid social on spend up to £10k'
        ]
    },
    {
        id: 'scale',
        name: 'Scale',
        blurb: 'For a team that wants a whole social department on call.',
        monthly: 5400,
        yearly: 4490,
        cta: 'Talk about Scale',
        highlighted: false,
        features: [
            '40+ posts a month',
            '20 short videos',
            'Up to 6 platforms',
            'A creator programme',
            'Community care seven days a week',
            'A weekly read and a live dashboard',
            'A weekly strategy call',
            'Paid social on any spend'
        ]
    }
]

export const everyPlan: Array<{ icon: 'gift' | 'file' | 'user' | 'calendar' | 'shield' | 'timer', title: string, body: string }> = [
    { icon: 'gift', title: 'A free forecast', body: 'A one page read of your category and three ideas, before you pay a penny.' },
    { icon: 'file', title: 'Month to month', body: 'After the first quarter, stay because it works. Not because of paperwork.' },
    { icon: 'user', title: 'One named strategist', body: 'A senior person who knows your brand and answers your messages.' },
    { icon: 'calendar', title: 'A shared calendar', body: 'See every post before it goes out. Comment, tweak or approve in a tap.' },
    { icon: 'shield', title: 'It is all yours', body: 'Every photo, video, caption and template belongs to you. Always.' },
    { icon: 'timer', title: 'Pause any month', body: 'Quiet season? Pause with 30 days notice and pick up where you left off.' }
]

export type FeatureValue = string | boolean

export const featureGroups: Array<{ group: string, rows: Array<{ label: string, values: [FeatureValue, FeatureValue, FeatureValue] }> }> = [
    {
        group: 'Making things',
        rows: [
            { label: 'Posts each month', values: ['12', '24', '40+'] },
            { label: 'Short videos', values: ['4', '10', '20'] },
            { label: 'Shoot days', values: ['Every quarter', 'Every month', 'Twice a month'] },
            { label: 'Platforms', values: ['2', '3', 'Up to 6'] }
        ]
    },
    {
        group: 'Thinking',
        rows: [
            { label: 'Strategy calls', values: ['Monthly', 'Twice a month', 'Weekly'] },
            { label: 'Weekly signal brief', values: [false, true, true] },
            { label: 'Look and voice', values: ['Templates', 'Full system', 'Full system and creator guide'] }
        ]
    },
    {
        group: 'Growing',
        rows: [
            { label: 'Community care', values: ['Weekdays', 'Every day', 'Seven days, evenings too'] },
            { label: 'Paid social', values: [false, 'Up to £10k spend', 'Any spend'] },
            { label: 'Creator programme', values: [false, 'As an extra', true] }
        ]
    },
    {
        group: 'Reading the room',
        rows: [
            { label: 'Performance reads', values: ['Monthly', 'Every two weeks', 'Weekly'] },
            { label: 'Live dashboard', values: [false, true, true] },
            { label: 'Quarterly forecast workshop', values: [false, false, true] }
        ]
    }
]

export const pricingFaqs: Array<Faq> = [
    {
        question: 'What is in the free forecast?',
        answer: 'A one page read of where your category is heading, three content ideas built for it, the platforms we would focus on and the times your audience is around. It is yours to keep, whether you hire us or not.'
    },
    {
        question: 'Is there a long contract?',
        answer: 'The first quarter is a commitment, because good series take a few weeks to find their feet. After that, every plan runs month to month with 30 days notice.'
    },
    {
        question: 'Is ad spend included?',
        answer: 'No. Plan prices cover the thinking, the making, the management and the reporting. Ad spend goes straight to the platforms, so you can see every pound.'
    },
    {
        question: 'Can we change plans later?',
        answer: 'Any month. Lots of brands start on Starter, find their rhythm and move to Growth once the series are working.'
    },
    {
        question: 'How does yearly billing work?',
        answer: 'Pay for twelve months up front and you save about 18% against monthly. You can still pause for one month in the year if business gets quiet.'
    }
]
